import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, TrendingUp, Wallet, ReceiptEuro, AlertTriangle, Users, ArrowLeft, Filter } from "lucide-react";
import { API, useAuth } from "@/contexts/AuthContext";

// Founder-only Finance/Revenue page. Reads GET /api/founder/revenue.
function fmtEUR(n, digits = 2) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return new Intl.NumberFormat("en-IE", {
    style: "currency", currency: "EUR", maximumFractionDigits: digits,
  }).format(Number(n));
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}
function fmtDay(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return "—"; }
}

export default function RevenuePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [drillEmail, setDrillEmail] = useState(null);

  // Customers filters (client-side; backend already returns everything).
  const [fPlan, setFPlan] = useState("");         // "" = any
  const [fStatus, setFStatus] = useState("");     // "" | "paid" | "trial"
  const [fDomain, setFDomain] = useState("");     // "" | "yes" | "no"
  const [fFrom, setFFrom] = useState("");         // YYYY-MM-DD
  const [fTo, setFTo] = useState("");             // YYYY-MM-DD

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/founder/revenue`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Could not load revenue.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const drillTxns = useMemo(() => {
    if (!drillEmail || !data?.transactions) return [];
    return data.transactions.filter((t) => (t.email || "").toLowerCase() === drillEmail.toLowerCase());
  }, [drillEmail, data]);

  // Client-side filter for the customers list. The filter is intentionally
  // forgiving: an empty filter = "any". Plan match is a case-insensitive
  // contains against BOTH the derived-from-payments plan AND the user's
  // canonical subscription_plan (backend field `user_plan`), so a customer
  // whose latest payment is a domain but whose account subscription is
  // Kickstart 1 still matches when the founder picks "Kickstart 1".
  const filteredCustomers = useMemo(() => {
    const list = data?.customers || [];
    if (!list.length) return list;
    const planNeedle = fPlan.trim().toLowerCase();
    const domainOnly = planNeedle === "domain-only";
    return list.filter((c) => {
      if (planNeedle) {
        if (domainOnly) {
          if (!c.has_domain) return false;
          const paidHits = (c.plan || "").toLowerCase();
          const userHits = (c.user_plan || "").toLowerCase();
          // "Domain only" = has a domain AND no non-domain plan on
          // either side (no Kickstart / subscription).
          const nonDomainKw = ["kickstart", "compleet", "starter", "creator",
            "business", "agency", "enterprise", "ai+social", "site basis", "site plus"];
          const hasNonDomain = nonDomainKw.some((k) =>
            paidHits.includes(k) || userHits.includes(k)
          );
          if (hasNonDomain) return false;
        } else {
          const hay = `${c.plan || ""} ${c.user_plan || ""}`.toLowerCase();
          if (!hay.includes(planNeedle)) return false;
        }
      }
      if (fStatus === "trial" && !c.is_trial) return false;
      if (fStatus === "paid" && c.is_trial) return false;
      if (fDomain === "yes" && !c.has_domain) return false;
      if (fDomain === "no" && c.has_domain) return false;
      if (fFrom) {
        const fp = String(c.first_payment_at || "").slice(0, 10);
        if (!fp || fp < fFrom) return false;
      }
      if (fTo) {
        const fp = String(c.first_payment_at || "").slice(0, 10);
        if (!fp || fp > fTo) return false;
      }
      return true;
    });
  }, [data, fPlan, fStatus, fDomain, fFrom, fTo]);

  const resetFilters = () => {
    setFPlan(""); setFStatus(""); setFDomain(""); setFFrom(""); setFTo("");
  };
  const anyFilter = Boolean(fPlan || fStatus || fDomain || fFrom || fTo);

  if (!user?.is_founder) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center text-[#666]" data-testid="revenue-forbidden">
        Founder access only.
      </div>
    );
  }

  return (
    <div className="max-w-6xl" data-testid="revenue-page">
      <p className="zy-eyebrow mb-2">Founder · Finance</p>
      <h1 className="text-[28px] font-bold tracking-tight text-[#0A1628] flex items-center gap-2.5">
        <TrendingUp size={22} style={{ color: "#202B60" }} />
        Revenue
      </h1>
      <p className="text-[14px] text-[#555] mt-1">
        Kickstart and domain purchases are one-time — they never sit inside MRR/ARR.
      </p>

      {error && (
        <div data-testid="revenue-error" className="mt-6 rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {!data && !error && (
        <div className="mt-10 flex items-center text-[#666]">
          <Loader2 size={16} className="animate-spin mr-2" /> Loading…
        </div>
      )}

      {data && (
        <>
          {/* A. Revenue now */}
          <section className="mt-6 grid gap-3 sm:grid-cols-3">
            <SummaryCard testid="revenue-mrr" label="MRR" value={fmtEUR(data.mrr_eur)} note="Recurring · monthly" />
            <SummaryCard testid="revenue-arr" label="ARR" value={fmtEUR(data.arr_eur)} note="MRR × 12" />
            <SummaryCard
              testid="revenue-onetime-month"
              label="One-time this month"
              value={fmtEUR(data.one_time_this_month_eur)}
              note="Kickstart · domains · credit packs"
            />
          </section>

          {/* Active subscriptions breakdown */}
          {data.breakdown?.length > 0 && (
            <section className="mt-6">
              <SectionTitle icon={<Wallet size={14} />} title="Active subscriptions" />
              <div className="rounded-xl border border-[#eee] bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F7F8FA] text-[#666] text-[12px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left font-medium px-4 py-2">Plan</th>
                      <th className="text-right font-medium px-4 py-2">Active</th>
                      <th className="text-right font-medium px-4 py-2">Monthly price</th>
                      <th className="text-right font-medium px-4 py-2">Contribution</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F0F0F0]">
                    {data.breakdown.map((row) => (
                      <tr key={row.plan}>
                        <td className="px-4 py-2.5 text-[#0A1628] font-medium">{row.plan}</td>
                        <td className="px-4 py-2.5 text-right">{row.count}</td>
                        <td className="px-4 py-2.5 text-right">{fmtEUR(row.monthly_eur)}</td>
                        <td className="px-4 py-2.5 text-right font-semibold">{fmtEUR(row.revenue_eur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* B. Payments table (or drill-down if a customer row is selected) */}
          <section className="mt-8">
            {drillEmail ? (
              <>
                <button
                  type="button"
                  onClick={() => setDrillEmail(null)}
                  data-testid="drill-close"
                  className="mb-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[#202B60] hover:underline"
                >
                  <ArrowLeft size={13} /> Back to all payments
                </button>
                <SectionTitle icon={<ReceiptEuro size={14} />} title={`Payments · ${drillEmail}`} />
                <TxTable rows={drillTxns} onRowClick={null} />
              </>
            ) : (
              <>
                <SectionTitle icon={<ReceiptEuro size={14} />} title="Payments" />
                <TxTable rows={data.transactions || []} onRowClick={(em) => setDrillEmail(em)} />
              </>
            )}
          </section>

          {/* C. Customers */}
          {!drillEmail && (
            <section className="mt-8">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <SectionTitle icon={<Users size={14} />} title="Customers" />
                <p className="text-[12px] text-[#888]" data-testid="customers-count">
                  {filteredCustomers.length} of {(data.customers || []).length}
                </p>
              </div>

              {/* Filter bar */}
              <div
                className="rounded-xl border border-[#eee] bg-white p-3 mb-3 flex flex-wrap items-end gap-3"
                data-testid="customers-filters"
              >
                <div className="flex items-center gap-1.5 text-[12px] font-semibold text-[#666]">
                  <Filter size={12} /> Filter
                </div>
                <FilterField label="Plan">
                  <select
                    value={fPlan}
                    onChange={(e) => setFPlan(e.target.value)}
                    data-testid="filter-plan"
                    className="rounded-md border border-[#ddd] bg-white text-[13px] px-2 py-1.5 min-w-[150px]"
                  >
                    <option value="">Any</option>
                    <option value="kickstart 1">Kickstart 1</option>
                    <option value="kickstart 2">Kickstart 2</option>
                    <option value="kickstart 3">Kickstart 3</option>
                    <option value="starter">Starter</option>
                    <option value="creator">Creator</option>
                    <option value="business">Business</option>
                    <option value="agency">Agency</option>
                    <option value="enterprise">Enterprise</option>
                    <option value="domain-only">Domain-only</option>
                  </select>
                </FilterField>
                <FilterField label="Status">
                  <select
                    value={fStatus}
                    onChange={(e) => setFStatus(e.target.value)}
                    data-testid="filter-status"
                    className="rounded-md border border-[#ddd] bg-white text-[13px] px-2 py-1.5"
                  >
                    <option value="">Any</option>
                    <option value="paid">Paid</option>
                    <option value="trial">Trial</option>
                  </select>
                </FilterField>
                <FilterField label="Has domain">
                  <select
                    value={fDomain}
                    onChange={(e) => setFDomain(e.target.value)}
                    data-testid="filter-domain"
                    className="rounded-md border border-[#ddd] bg-white text-[13px] px-2 py-1.5"
                  >
                    <option value="">Any</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </FilterField>
                <FilterField label="Created from">
                  <input
                    type="date"
                    value={fFrom}
                    onChange={(e) => setFFrom(e.target.value)}
                    data-testid="filter-from"
                    className="rounded-md border border-[#ddd] bg-white text-[13px] px-2 py-1.5"
                  />
                </FilterField>
                <FilterField label="Created to">
                  <input
                    type="date"
                    value={fTo}
                    onChange={(e) => setFTo(e.target.value)}
                    data-testid="filter-to"
                    className="rounded-md border border-[#ddd] bg-white text-[13px] px-2 py-1.5"
                  />
                </FilterField>
                {anyFilter && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    data-testid="filter-reset"
                    className="rounded-full border border-[#ddd] bg-white px-3 py-1.5 text-[12.5px] font-semibold text-[#0A1628] hover:bg-[#F7F8FA]"
                  >
                    Reset
                  </button>
                )}
              </div>

              <div className="rounded-xl border border-[#eee] bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="customers-table">
                  <thead className="bg-[#F7F8FA] text-[#666] text-[12px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left font-medium px-4 py-2">Email</th>
                      <th className="text-left font-medium px-4 py-2">Latest plan</th>
                      <th className="text-left font-medium px-4 py-2">Status</th>
                      <th className="text-left font-medium px-4 py-2">Domain</th>
                      <th className="text-left font-medium px-4 py-2">First payment</th>
                      <th className="text-right font-medium px-4 py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F0F0F0]">
                    {filteredCustomers.map((c) => (
                      <tr
                        key={c.email}
                        onClick={() => setDrillEmail(c.email)}
                        data-testid={`customer-row-${c.email}`}
                        className="cursor-pointer hover:bg-[#F7F8FA]"
                      >
                        <td className="px-4 py-2.5 text-[#0A1628] font-medium">{c.email}</td>
                        <td className="px-4 py-2.5 text-[#0A1628]">{c.plan || c.user_plan || "—"}</td>
                        <td className="px-4 py-2.5">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11.5px] font-semibold ${
                              c.is_trial
                                ? "border-amber-200 bg-amber-50 text-amber-700"
                                : "border-emerald-200 bg-emerald-50 text-emerald-700"
                            }`}
                            data-testid={`customer-status-${c.email}`}
                          >
                            {c.is_trial ? "Trial" : "Paid"}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-[#666]">{c.has_domain ? "Yes" : "—"}</td>
                        <td className="px-4 py-2.5 text-[#666]">{fmtDay(c.first_payment_at)}</td>
                        <td className="px-4 py-2.5 text-right font-semibold">{fmtEUR(c.total_eur)}</td>
                      </tr>
                    ))}
                    {filteredCustomers.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-[#888]">
                        {(data.customers || []).length === 0
                          ? "No paying customers yet."
                          : "No customers match these filters."}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* D. Failed / open */}
          {!drillEmail && (
            <section className="mt-8">
              <SectionTitle icon={<AlertTriangle size={14} className="text-amber-600" />} title="Failed & abandoned" />
              <div className="rounded-xl border border-[#eee] bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="failed-table">
                  <thead className="bg-[#F7F8FA] text-[#666] text-[12px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left font-medium px-4 py-2">Date</th>
                      <th className="text-left font-medium px-4 py-2">Kind</th>
                      <th className="text-left font-medium px-4 py-2">Email</th>
                      <th className="text-right font-medium px-4 py-2">Amount</th>
                      <th className="text-left font-medium px-4 py-2">Stripe id</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F0F0F0]">
                    {(data.failed_payments || []).map((f) => (
                      <tr key={f.id}>
                        <td className="px-4 py-2.5 text-[#666] whitespace-nowrap">{fmtDate(f.created_at)}</td>
                        <td className="px-4 py-2.5 text-[#0A1628]">{f.kind}</td>
                        <td className="px-4 py-2.5">{f.customer_email || "—"}</td>
                        <td className="px-4 py-2.5 text-right">{fmtEUR(f.amount_eur)}</td>
                        <td className="px-4 py-2.5 text-[#666] font-mono text-[11.5px]">{f.stripe_id || "—"}</td>
                      </tr>
                    ))}
                    {(data.failed_payments || []).length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-[#888]">No failed or abandoned payments recorded.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ testid, label, value, note }) {
  return (
    <div className="rounded-xl border border-[#eee] bg-white p-5" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wider font-semibold text-[#666]">{label}</p>
      <p className="mt-1 text-[24px] font-bold text-[#0A1628]">{value}</p>
      <p className="text-[12px] text-[#888] mt-1">{note}</p>
    </div>
  );
}
function SectionTitle({ icon, title }) {
  return (
    <h2 className="text-[13px] font-semibold text-[#0A1628] mb-2 flex items-center gap-1.5">
      {icon} {title}
    </h2>
  );
}
function FilterField({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wider text-[#666] font-semibold">{label}</span>
      {children}
    </label>
  );
}
function TxTable({ rows, onRowClick }) {
  return (
    <div className="rounded-xl border border-[#eee] bg-white overflow-hidden">
      <table className="w-full text-sm" data-testid="revenue-tx-table">
        <thead className="bg-[#F7F8FA] text-[#666] text-[12px] uppercase tracking-wider">
          <tr>
            <th className="text-left font-medium px-4 py-2">Date</th>
            <th className="text-left font-medium px-4 py-2">Email</th>
            <th className="text-left font-medium px-4 py-2">Product</th>
            <th className="text-left font-medium px-4 py-2">Type</th>
            <th className="text-right font-medium px-4 py-2">Amount</th>
            <th className="text-left font-medium px-4 py-2">Stripe id</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F0F0F0]">
          {rows.map((t) => (
            <tr
              key={t.session_id}
              onClick={onRowClick ? () => onRowClick(t.email) : undefined}
              data-testid={`tx-row-${t.session_id}`}
              className={onRowClick ? "cursor-pointer hover:bg-[#F7F8FA]" : ""}
            >
              <td className="px-4 py-2.5 text-[#666] whitespace-nowrap">{fmtDate(t.date)}</td>
              <td className="px-4 py-2.5 text-[#0A1628]">{t.email || "—"}</td>
              <td className="px-4 py-2.5 text-[#0A1628]">{t.product}</td>
              <td className="px-4 py-2.5">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11.5px] font-semibold ${t.type === "subscription" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
                  {t.type === "subscription" ? "Subscription" : "One-time"}
                </span>
              </td>
              <td className="px-4 py-2.5 text-right font-semibold text-[#0A1628] whitespace-nowrap">{fmtEUR(t.amount_eur)}</td>
              <td className="px-4 py-2.5 text-[#666] font-mono text-[11.5px] truncate max-w-[220px]">{t.session_id || "—"}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={6} className="px-4 py-8 text-center text-[#888]">No paid transactions yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
